/**
 * Cursor Soccer field variants — NORMAL (current) and MEGA FIELD.
 * Shared by server sim and client render/input.
 */

export type SoccerVariantId = "normal" | "mega";

export type SoccerPickupKind = "max" | "small";

export type SoccerPickupDef = {
  id: string;
  kind: SoccerPickupKind;
  x: number;
  y: number;
};

export type SoccerVariantConfig = {
  id: SoccerVariantId;
  label: string;
  w: number;
  h: number;
  ballR: number;
  cursorR: number;
  matchSec: number;
  goalMouthH: number;
  goalDepth: number;
  postR: number;
  goalInset: number;
  kickoffSpread: number;
  /** Absolute mouse positioning (NORMAL) vs speed-capped chase (MEGA). */
  speedCapped: boolean;
  /** World units / sec — goal-line to goal-line ≈ w / maxSpeed seconds. */
  maxSpeed: number;
  boostMaxSpeed: number;
  /** Acceleration toward the stick/mouse target (ice skate feel). */
  accel: number;
  boostAccel: number;
  /** Velocity retention per second while coasting (0–1, higher = more ice). */
  coastRetain: number;
  /** Charge units / sec while boost held (1 = full bar). */
  boostDrainPerSec: number;
  smallBoostGain: number;
  pickupRadius: number;
  pickupRespawnSec: number;
  ballRollDecel: number;
  ballRollDecelSlow: number;
  maxBallSpeed: number;
  cursorRest: number;
  cursorMass: number;
  ballMass: number;
  cursorPushGain: number;
  cursorTouchSlack: number;
  spinCurve: number;
  spinFromHit: number;
  pickups: SoccerPickupDef[];
};

const NORMAL_W = 1600;
const NORMAL_H = 900;
/** ~3× stadium — cursors read smaller; ball/goals scale up in world units. */
const MEGA_W = 4800;
const MEGA_H = 2700;

function megaPickups(): SoccerPickupDef[] {
  const insetX = MEGA_W * 0.08;
  const insetY = MEGA_H * 0.1;
  const maxes: SoccerPickupDef[] = [
    { id: "max-tl", kind: "max", x: insetX, y: insetY },
    { id: "max-tr", kind: "max", x: MEGA_W - insetX, y: insetY },
    { id: "max-bl", kind: "max", x: insetX, y: MEGA_H - insetY },
    { id: "max-br", kind: "max", x: MEGA_W - insetX, y: MEGA_H - insetY },
  ];
  const smalls: SoccerPickupDef[] = [];
  const xs = [0.18, 0.32, 0.42, 0.5, 0.58, 0.68, 0.82];
  const ys = [0.22, 0.38, 0.5, 0.62, 0.78];
  let n = 0;
  for (const xf of xs) {
    for (const yf of ys) {
      if (Math.abs(xf - 0.5) < 0.04 && Math.abs(yf - 0.5) < 0.08) continue;
      smalls.push({
        id: `small-${n++}`,
        kind: "small",
        x: MEGA_W * xf,
        y: MEGA_H * yf,
      });
    }
  }
  return [...maxes, ...smalls];
}

export const SOCCER_VARIANT_NORMAL: SoccerVariantConfig = {
  id: "normal",
  label: "NORMAL",
  w: NORMAL_W,
  h: NORMAL_H,
  ballR: 36,
  cursorR: 15,
  matchSec: 120,
  goalMouthH: 268,
  goalDepth: 88,
  postR: 11,
  goalInset: 132,
  kickoffSpread: 180,
  speedCapped: false,
  maxSpeed: 0,
  boostMaxSpeed: 0,
  accel: 0,
  boostAccel: 0,
  coastRetain: 0,
  boostDrainPerSec: 0,
  smallBoostGain: 0,
  pickupRadius: 0,
  pickupRespawnSec: 10,
  ballRollDecel: 300,
  ballRollDecelSlow: 220,
  maxBallSpeed: 1400,
  cursorRest: 0.55,
  cursorMass: 1,
  ballMass: 2.8,
  cursorPushGain: 22,
  cursorTouchSlack: 26,
  spinCurve: 0.000032,
  spinFromHit: 0.002,
  pickups: [],
};

export const SOCCER_VARIANT_MEGA: SoccerVariantConfig = {
  id: "mega",
  label: "MEGA FIELD",
  w: MEGA_W,
  h: MEGA_H,
  ballR: 78,
  cursorR: 15,
  matchSec: 180,
  goalMouthH: 520,
  goalDepth: 220,
  postR: 18,
  goalInset: 220,
  kickoffSpread: 360,
  speedCapped: true,
  /** ~5s goal-to-goal without boost */
  maxSpeed: MEGA_W / 5,
  /** ~3s with boost (noticeable, not teleport) */
  boostMaxSpeed: MEGA_W / 3,
  accel: 2100,
  boostAccel: 3400,
  coastRetain: 0.82,
  boostDrainPerSec: 1 / 3,
  smallBoostGain: 0.18,
  pickupRadius: 55,
  pickupRespawnSec: 10,
  ballRollDecel: 185,
  ballRollDecelSlow: 125,
  maxBallSpeed: 1700,
  cursorRest: 0.45,
  cursorMass: 1,
  ballMass: 2.2,
  cursorPushGain: 28,
  cursorTouchSlack: 42,
  spinCurve: 0.000035,
  spinFromHit: 0.0022,
  pickups: megaPickups(),
};

export function soccerVariantConfig(id: SoccerVariantId | string | null | undefined): SoccerVariantConfig {
  return id === "mega" ? SOCCER_VARIANT_MEGA : SOCCER_VARIANT_NORMAL;
}

export function parseSoccerVariant(raw: unknown): SoccerVariantId {
  return raw === "mega" ? "mega" : "normal";
}
